/**
 * OG Image Spec Builder — builds json-render Specs for OG image generation
 *
 * Creates a json-render Spec (same format as the React renderer) but composed
 * of image-compatible components for satori rendering.
 *
 * Uses the standard @json-render/image layout components (Frame, Row, Column,
 * Text, Box, Divider) plus custom ddCatalog image components (DramaScore,
 * DramaCategoryBadge, NarrativeBlock).
 *
 * The og-image SDUI surface config controls which elements are shown.
 *
 * Task: [#1158.P1.4] OG Image Spec Builder
 * Agent: Diego <diego@liftping.net>
 */

import type { GenerationCoreOutput } from "@dramadetector/shared/content-generation";
import type { SDUIOGImageConfig } from "./sdui-defaults";
import { SDUI_DEFAULTS } from "./sdui-defaults";
import { CATEGORY_COLORS, CATEGORY_LABELS } from "./image-registry";

// ============================================================================
// Types
// ============================================================================

export interface OGImageData {
	score: number;
	category: string;
	summary: string;
	patternsCount?: number;
	/** Template variant hint from the decision engine (e.g., "rewrite-journey") */
	variant?: "rewrite-journey" | "default";
}

// Minimal Spec type matching @json-render/core Spec structure
interface SpecElement {
	type: string;
	props: Record<string, unknown>;
	children: string[];
}

interface OGImageSpec {
	root: string;
	elements: Record<string, SpecElement>;
}

// ============================================================================
// Spec builder
// ============================================================================

/**
 * Build a json-render Spec for an OG image from analysis data.
 *
 * The spec uses standard @json-render/image components (Frame, Row, Column,
 * Text, Box) combined with custom DD image components (DramaScore,
 * DramaCategoryBadge, NarrativeBlock).
 *
 * @param data - Analysis data (score, category, summary)
 * @param config - SDUI og-image surface config (defaults used if omitted)
 */
export function buildOGImageSpec(
	data: OGImageData,
	config?: Partial<SDUIOGImageConfig>,
): OGImageSpec {
	const cfg: SDUIOGImageConfig = { ...SDUI_DEFAULTS["og-image"], ...config };
	const category = data.category || "safe";
	const colors = CATEGORY_COLORS[category] ?? CATEGORY_COLORS.safe;
	const categoryLabel = CATEGORY_LABELS[category] ?? "Analysis";
	const footerText = cfg.footerText ?? "AI-Powered Communication Analysis";

	// Truncate summary to configured max length
	const summary =
		data.summary && data.summary.length > cfg.maxSummaryLength
			? data.summary.slice(0, cfg.maxSummaryLength - 3) + "..."
			: data.summary || "Communication analysis by DramaDetector";

	// Build elements map
	const elements: Record<string, SpecElement> = {};
	const rootChildren: string[] = [];

	// Header row: brand name + category badge
	elements["header-brand"] = {
		type: "Text",
		props: {
			text: "DramaDetector",
			fontSize: 32,
			fontWeight: "bold",
			color: "#1F2937",
			letterSpacing: "-0.02em",
		},
		children: [],
	};

	elements["header-badge"] = {
		type: "DramaCategoryBadge",
		props: { category },
		children: [],
	};

	elements["header"] = {
		type: "Row",
		props: {
			justifyContent: "space-between",
			alignItems: "center",
		},
		children: ["header-brand", "header-badge"],
	};
	rootChildren.push("header");

	// Spacer after header
	elements["spacer-1"] = {
		type: "Spacer",
		props: { height: 32 },
		children: [],
	};
	rootChildren.push("spacer-1");

	// Score section (gated by config)
	if (cfg.showScore) {
		elements["score"] = {
			type: "DramaScore",
			props: {
				score: data.score,
				category,
				size: data.score >= 7 ? "lg" : "md",
				showLabel: true,
			},
			children: [],
		};

		elements["score-context"] = {
			type: "Text",
			props: {
				text: `${categoryLabel} Level`,
				fontSize: 18,
				color: colors.text,
				fontWeight: "normal",
			},
			children: [],
		};

		elements["score-row"] = {
			type: "Row",
			props: {
				alignItems: "center",
				gap: 24,
			},
			children: ["score", "score-context"],
		};
		rootChildren.push("score-row");

		elements["spacer-2"] = {
			type: "Spacer",
			props: { height: 20 },
			children: [],
		};
		rootChildren.push("spacer-2");
	}

	// Summary text (gated by config)
	if (cfg.showSummary) {
		elements["summary"] = {
			type: "NarrativeBlock",
			props: {
				text: summary,
				emphasis: category === "extreme_drama" ? "critical" : "normal",
			},
			children: [],
		};

		// Wrap in a Box with flex:1 so it takes remaining space
		elements["summary-box"] = {
			type: "Box",
			props: {
				flex: 1,
			},
			children: ["summary"],
		};
		rootChildren.push("summary-box");
	}

	// Patterns count indicator (if available)
	if (data.patternsCount && data.patternsCount > 0) {
		elements["patterns-count"] = {
			type: "Text",
			props: {
				text: `${data.patternsCount} pattern${data.patternsCount === 1 ? "" : "s"} detected`,
				fontSize: 14,
				color: colors.accent,
				fontWeight: "bold",
			},
			children: [],
		};
		rootChildren.push("patterns-count");
	}

	// Footer divider
	elements["footer-divider"] = {
		type: "Divider",
		props: {
			color: `${colors.accent}30`,
			thickness: 2,
			marginTop: 12,
			marginBottom: 12,
		},
		children: [],
	};
	rootChildren.push("footer-divider");

	// Footer row: site name + tagline
	elements["footer-site"] = {
		type: "Text",
		props: {
			text: "dramadetector.com",
			fontSize: 18,
			color: "#6B7280",
		},
		children: [],
	};

	elements["footer-tagline"] = {
		type: "Text",
		props: {
			text: footerText,
			fontSize: 16,
			color: "#9CA3AF",
		},
		children: [],
	};

	elements["footer"] = {
		type: "Row",
		props: {
			justifyContent: "space-between",
			alignItems: "center",
		},
		children: ["footer-site", "footer-tagline"],
	};
	rootChildren.push("footer");

	// Root frame
	elements["root"] = {
		type: "Frame",
		props: {
			width: 1200,
			height: 630,
			backgroundColor: colors.bg,
			padding: 60,
			display: "flex",
			flexDirection: "column",
		},
		children: rootChildren,
	};

	return {
		root: "root",
		elements,
	};
}

export function buildOGImageSpecFromGenerationOutput(
	output: GenerationCoreOutput,
	config?: Partial<SDUIOGImageConfig>,
): OGImageSpec {
	const summaryText =
		typeof output.resolved.variables.summaryText === "string"
			? output.resolved.variables.summaryText
			: "Communication analysis by DramaDetector";

	const categoryKey =
		typeof output.resolved.variables.categoryKey === "string"
			? output.resolved.variables.categoryKey
			: "safe";

	const scoreValue = Number(
		output.resolved.variables.scoreValue ??
			output.resolved.variables.scoreLabel ??
			0,
	);
	const patternsValue =
		typeof output.resolved.variables.patternsCount === "number"
			? output.resolved.variables.patternsCount
			: undefined;

	return buildOGImageSpec(
		{
			score: Number.isFinite(scoreValue) ? scoreValue : 0,
			category: categoryKey,
			summary: summaryText,
			patternsCount: patternsValue,
		},
		config,
	);
}
